import React, { useState, useRef } from 'react';
import Barcode from 'react-barcode';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';

// Tip tanımlamasını düzeltelim
type BarcodeFormat = 'EAN13' | 'CODE128';

const BarcodeGenerator: React.FC = () => {
  const [productCode, setProductCode] = useState<string>('');
  const [barcode, setBarcode] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isPrinting, setIsPrinting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const barcodeRef = useRef<HTMLDivElement>(null);
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>('EAN13');

  const INITIAL_CODE = '869362';
  const CODE128_PREFIX = 'NXK-';

  const calculateCheckDigit = (code: string): number => {
    let sum = 0;
    for (let i = 0; i < 12; i++) {
      const digit = parseInt(code[i]);
      sum += (i % 2 === 0) ? digit : digit * 3;
    }
    const checkDigit = (10 - (sum % 10)) % 10;
    return checkDigit;
  };

  const generateRandomCode = (): string => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const generateBarcode = (customCode?: string) => {
    const code = customCode || productCode;
    
    if (code.length !== 6) {
      setError('Lütfen 6 haneli bir ürün kodu girin');
      setBarcode('');
      return;
    }

    if (!/^\d+$/.test(code)) {
      setError('Lütfen sadece sayı girin');
      setBarcode('');
      return;
    }

    // Format kontrollerini düzeltiyoruz
    if (barcodeFormat === 'EAN13') {
      // EAN-13 barkodu oluştur
      const code12 = INITIAL_CODE + code;
      const checkDigit = calculateCheckDigit(code12);
      const finalBarcode = code12 + checkDigit;
      setBarcode(finalBarcode);
    } else if (barcodeFormat === 'CODE128') {
      // CODE 128 barkodu oluştur
      const finalBarcode = CODE128_PREFIX + code;
      setBarcode(finalBarcode);
    }
    
    setError('');
  };

  const generateRandomBarcode = () => {
    const randomCode = generateRandomCode();
    setProductCode(randomCode);
    generateBarcode(randomCode);
  };

  // PNG olarak indirme fonksiyonu
  const downloadBarcode = async () => {
    if (barcodeRef.current) {
      try {
        setIsExporting(true);
        const canvas = await html2canvas(barcodeRef.current);
        const url = canvas.toDataURL('image/png');
        const link = document.createElement('a');
        link.download = `barcode-${barcode}.png`;
        link.href = url;
        link.click();
      } catch (error) {
        console.error('PNG oluşturma hatası:', error);
        setError('PNG olarak kaydederken bir hata oluştu.');
      } finally {
        setIsExporting(false);
      }
    }
  };

  // Yeni: SVG olarak indirme fonksiyonu
  const downloadBarcodeSVG = () => {
    if (barcodeRef.current) {
      try {
        setIsExporting(true);
        
        // Barkod container'ı içindeki SVG elementlerini bul
        const svgElements = barcodeRef.current.querySelectorAll('svg');
        
        if (svgElements.length === 0) {
          setError('SVG elementleri bulunamadı.');
          return;
        }
        
        // Tüm içeriği kapsayan bir SVG oluştur
        const svgNamespace = "http://www.w3.org/2000/svg";
        const combinedSvg = document.createElementNS(svgNamespace, "svg");
        combinedSvg.setAttribute("xmlns", svgNamespace);
        combinedSvg.setAttribute("width", "500");
        combinedSvg.setAttribute("height", "400");
        combinedSvg.setAttribute("viewBox", "0 0 500 400");
        
        // Arkaplan oluştur
        const background = document.createElementNS(svgNamespace, "rect");
        background.setAttribute("width", "500");
        background.setAttribute("height", "400");
        background.setAttribute("fill", "white");
        combinedSvg.appendChild(background);
        
        // QR code ve barkodu kopyala
        let svgIndex = 0;
        
        // İlk SVG (QR Code) için
        if (svgIndex < svgElements.length) {
          const qrSvg = svgElements[svgIndex] as SVGElement;
          const qrGroup = document.createElementNS(svgNamespace, "g");
          qrGroup.setAttribute("transform", "translate(160, 30)");
          
          const qrClone = qrSvg.cloneNode(true) as SVGElement;
          qrClone.removeAttribute("style");
          qrGroup.appendChild(qrClone);
          combinedSvg.appendChild(qrGroup);
          svgIndex++;
        }
        
        // İkinci SVG (Barkod) için
        if (svgIndex < svgElements.length) {
          const barcodeSvg = svgElements[svgIndex] as SVGElement;
          const barcodeGroup = document.createElementNS(svgNamespace, "g");
          barcodeGroup.setAttribute("transform", "translate(100, 250)");
          
          const barcodeClone = barcodeSvg.cloneNode(true) as SVGElement;
          barcodeClone.removeAttribute("style");
          barcodeGroup.appendChild(barcodeClone);
          combinedSvg.appendChild(barcodeGroup);
        }
        
        // Barkod metni ekle
        const textElement = document.createElementNS(svgNamespace, "text");
        textElement.setAttribute("x", "250");
        textElement.setAttribute("y", "320");
        textElement.setAttribute("text-anchor", "middle");
        textElement.setAttribute("font-family", "monospace");
        textElement.setAttribute("font-size", "14");
        textElement.textContent = barcode;
        combinedSvg.appendChild(textElement);
        
        // SVG'yi indirmek için link oluştur
        const svgData = new XMLSerializer().serializeToString(combinedSvg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml' });
        const svgUrl = URL.createObjectURL(svgBlob);
        
        const link = document.createElement('a');
        link.download = `barcode-${barcode}.svg`;
        link.href = svgUrl;
        link.click();
        
        // URL nesnesini temizle
        setTimeout(() => {
          URL.revokeObjectURL(svgUrl);
        }, 100);
        
      } catch (error) {
        console.error('SVG oluşturma hatası:', error);
        setError('SVG olarak kaydederken bir hata oluştu.');
      } finally {
        setIsExporting(false);
      }
    }
  };

  // Mevcut yazdırma fonksiyonu - değişmeden kalıyor
  const printBarcode = async () => {
    if (!barcodeRef.current) return;
    
    try {
      setIsPrinting(true);
      
      const canvas = await html2canvas(barcodeRef.current, {
        scale: 2,
        backgroundColor: '#FFFFFF',
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      
      // Yazdırma için yeni bir pencere aç
      const printWindow = window.open('', '', 'width=600,height=600');
      if (!printWindow) {
        setIsPrinting(false);
        setError('Pop-up pencereler engelleniyor olabilir. Lütfen tarayıcı ayarlarınızı kontrol edin.');
        return;
      }
      
      // Yazdırma sayfası HTML'ini oluştur
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Barkod Yazdır</title>
            <style>
              body {
                margin: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                height: 100vh;
                font-family: Arial, sans-serif;
              }
              .print-container {
                text-align: center;
              }
              .barcode-image {
                max-width: 100%;
                height: auto;
              }
              .barcode-number {
                font-family: monospace;
                font-size: 14px;
                margin-top: 8px;
              }
              @media print {
                @page {
                  size: auto;
                  margin: 10mm;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <img src="${imgData}" class="barcode-image" />
            </div>
            <script>
              window.onload = function() {
                setTimeout(function() {
                  window.print();
                  setTimeout(function() { window.close(); }, 500);
                }, 300);
              };
            </script>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      
    } catch (error) {
      console.error('Yazdırma hatası:', error);
      setError('Yazdırma sırasında bir hata oluştu.');
    } finally {
      setIsPrinting(false);
    }
  };

  // Format değiştirme işlevini ekleyelim (bu satır eksik olabilir)
  const handleFormatChange = (format: BarcodeFormat) => {
    setBarcodeFormat(format);
    setBarcode(''); // Format değiştiğinde barkodu sıfırla
    
    if (productCode) {
      // Eğer girilen bir kod varsa, yeni formatla yeniden oluştur
      setTimeout(() => generateBarcode(), 0);
    }
  };

  // Placeholder ve bilgileri de düzeltiyoruz
  const getPlaceholderAndInfo = () => {
    if (barcodeFormat === 'EAN13') {
      return {
        placeholder: '6 haneli kod girin',
        info: `Başlangıç: ${INITIAL_CODE}`,
        description: 'EAN-13 formatı 13 haneli bir sayı oluşturur.'
      };
    } else {
      return {
        placeholder: '6 haneli kod girin',
        info: `Başlangıç: ${CODE128_PREFIX}`,
        description: 'Code 128 formatı alfanümerik değerler içerebilir.'
      };
    }
  };

  const { placeholder, info, description } = getPlaceholderAndInfo();

  return (
    <div className="py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="px-8 py-10">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-light text-gray-800 mb-3">Barkod Oluşturucu</h1>
            <div className="text-sm text-gray-500 bg-gray-50 rounded-full px-4 py-2 inline-block">
              {info}
            </div>
            <p className="text-xs text-gray-500 mt-2">{description}</p>
          </div>
          
          <div className="space-y-6">
            {/* Format seçim bölümü */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-600 mb-3">
                Barkod Formatı
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => handleFormatChange('EAN13')}
                  className={`flex-1 py-3 px-4 rounded-lg border font-medium transition-colors duration-200 
                    ${barcodeFormat === 'EAN13' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  EAN-13
                </button>
                <button
                  onClick={() => handleFormatChange('CODE128')}
                  className={`flex-1 py-3 px-4 rounded-lg border font-medium transition-colors duration-200 
                    ${barcodeFormat === 'CODE128' 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                >
                  Code 128
                </button>
              </div>
            </div>

            {/* Ürün kodu giriş alanı */}
            <div>
              <label htmlFor="productCode" className="block text-sm font-medium text-gray-600 mb-2">
                Ürün Kodu
              </label>
              <input
                type="text"
                id="productCode"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
                maxLength={6}
                className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition duration-200"
                placeholder={placeholder}
              />
            </div>

            {error && (
              <div className="bg-red-50 text-red-600 text-sm py-2 px-3 rounded-lg">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => generateBarcode()}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition duration-200 font-medium"
              >
                Oluştur
              </button>
              
              <button
                onClick={generateRandomBarcode}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg hover:bg-green-700 transition duration-200 font-medium"
              >
                Rastgele Kod Oluştur
              </button>
            </div>
              
            {barcode && (
              <div className="mt-8 space-y-6">
                <div ref={barcodeRef} className="bg-white p-8 border border-gray-200 rounded-lg">
                  <div className="flex flex-col items-center gap-12">
                    {/* QR Code - normal bileşen olarak bırakıldı */}
                    <div className="flex justify-center">
                      <QRCodeSVG
                        value={barcode}
                        size={180}
                        level="H"
                        includeMargin={true}
                      />
                    </div>
                    
                    {/* Barcode Container - normal bileşen olarak bırakıldı */}
                    <div className="flex flex-col items-center">
                      <div className="mb-4">
                        <Barcode
                          value={barcode}
                          width={2}
                          height={80}
                          displayValue={false}
                          margin={0}
                          background="#FFFFFF"
                          lineColor="#000000"
                          format={barcodeFormat}
                        />
                      </div>

                      {/* Numbers Row - Format-specific display */}
                      {barcodeFormat === 'EAN13' ? (
                        <div className="flex justify-between w-full font-mono text-sm space-x-4">
                          <span>{barcode[0]}</span>
                          <span>{barcode.slice(1, 7)}</span>
                          <span>{barcode.slice(7)}</span>
                        </div>
                      ) : (
                        <div className="font-mono text-sm">
                          {barcode}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Butonlar - 3 kolon olarak düzenleniyor */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* PNG olarak indir butonu */}
                  <button
                    onClick={downloadBarcode}
                    disabled={isExporting}
                    className={`py-3 px-2 rounded-lg flex items-center justify-center space-x-2 group font-medium 
                      ${isExporting 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100 transition duration-200 border border-gray-200'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:text-blue-600 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="group-hover:text-blue-600 transition-colors duration-200">PNG Olarak İndir</span>
                  </button>
                  
                  {/* SVG olarak indir butonu - YENİ */}
                  <button
                    onClick={downloadBarcodeSVG}
                    disabled={isExporting}
                    className={`py-3 px-2 rounded-lg flex items-center justify-center space-x-2 group font-medium 
                      ${isExporting 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-green-50 text-green-700 hover:bg-green-100 transition duration-200 border border-green-200'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:text-green-800 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    <span className="group-hover:text-green-800 transition-colors duration-200">SVG Olarak İndir</span>
                  </button>
                  
                  {/* Barkodu yazdır butonu */}
                  <button
                    onClick={printBarcode}
                    disabled={isPrinting}
                    className={`py-3 px-2 rounded-lg flex items-center justify-center space-x-2 group font-medium 
                      ${isPrinting 
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                        : 'bg-purple-50 text-purple-700 hover:bg-purple-100 transition duration-200 border border-purple-200'}`}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 group-hover:text-purple-800 transition-colors duration-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                    </svg>
                    <span className="group-hover:text-purple-800 transition-colors duration-200">
                      {isPrinting ? 'Yazdırılıyor...' : 'Barkodu Yazdır'}
                    </span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BarcodeGenerator;